import { useState } from "react";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useVehicleDocuments, VEHICLE_DOC_TYPES } from "@/hooks/useVehicles";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import type { Database } from "@/integrations/supabase/types";
import { UnifiedDocumentLinkDialog, type DocConfig } from "@/components/shared/UnifiedDocumentLinkDialog";

type VehicleDocumentType = Database["public"]["Enums"]["vehicle_document_type"];

interface AddAndLinkVehicleDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicleId: string;
  existingDocTypes: VehicleDocumentType[];
}

export function AddAndLinkVehicleDocumentDialog({
  open,
  onOpenChange,
  vehicleId,
  existingDocTypes,
}: AddAndLinkVehicleDocumentDialogProps) {
  const queryClient = useQueryClient();
  const { addDocument } = useVehicleDocuments(vehicleId);
  
  // Step management
  const [selectedType, setSelectedType] = useState<VehicleDocumentType | "">("");
  const [step, setStep] = useState<"select-type" | "link-upload">("select-type");
  const [isAddingWithoutDoc, setIsAddingWithoutDoc] = useState(false);

  // Filter out already existing document types
  const availableTypes = (Object.entries(VEHICLE_DOC_TYPES) as [VehicleDocumentType, typeof VEHICLE_DOC_TYPES[VehicleDocumentType]][])
    .filter(([key]) => !existingDocTypes.includes(key));

  const handleReset = () => {
    setSelectedType("");
    setStep("select-type");
    setIsAddingWithoutDoc(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !isAddingWithoutDoc) {
      handleReset();
    }
    onOpenChange(newOpen);
  };

  const handleTypeSelect = () => {
    if (!selectedType) return;
    setStep("link-upload");
  };

  // Add without document (just track the type)
  const handleAddWithoutDocument = async () => {
    if (!selectedType) return;
    
    setIsAddingWithoutDoc(true);
    try {
      await addDocument.mutateAsync({
        vehicle_id: vehicleId,
        doc_type: selectedType,
        label: VEHICLE_DOC_TYPES[selectedType].label,
        expires_at: null,
        status: "missing",
        document_id: null,
        valid_from: null,
        notes: null,
      });

      toast({
        title: "Document ajouté",
        description: `${VEHICLE_DOC_TYPES[selectedType].label} a été ajouté à suivre`,
      });

      handleOpenChange(false);
    } catch (error: any) {
      console.error("Add error:", error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'ajouter le document",
        variant: "destructive",
      });
    } finally {
      setIsAddingWithoutDoc(false);
    }
  };

  // Handle success from UnifiedDocumentLinkDialog
  const handleLinkSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["vehicle-documents", vehicleId] });
    queryClient.invalidateQueries({ queryKey: ["vehicles"] });
    handleOpenChange(false);
  };

  // Convert VEHICLE_DOC_TYPES config to DocConfig format
  const getDocConfig = (): DocConfig | null => {
    if (!selectedType) return null;
    const config = VEHICLE_DOC_TYPES[selectedType];
    return {
      key: selectedType,
      label: config.label,
      doc_type: "other",
      validityDays: config.validityDays,
    };
  };

  const docConfig = getDocConfig();

  // Step 2: Use UnifiedDocumentLinkDialog directly
  if (step === "link-upload" && docConfig) {
    return (
      <UnifiedDocumentLinkDialog
        open={open}
        onOpenChange={(newOpen: boolean) => {
          if (!newOpen) {
            handleReset();
            onOpenChange(false);
          }
        }}
        entityType="vehicle"
        entityId={vehicleId}
        docConfig={docConfig}
        linkTableName="vehicle_documents"
        onSuccess={handleLinkSuccess}
        showValidityDates={!!docConfig.validityDays}
        customHeader={
          <div className="pb-4 border-b mb-4">
            <Button
              variant="outline"
              onClick={handleAddWithoutDocument}
              disabled={isAddingWithoutDoc}
              className="w-full"
              size="sm"
            >
              {isAddingWithoutDoc ? "Ajout en cours..." : "Ajouter sans document (à compléter plus tard)"}
            </Button>
          </div>
        }
      />
    );
  }

  // Step 1: Type selection dialog
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Ajouter un document
          </DialogTitle>
          <DialogDescription>
            Sélectionnez le type de document à ajouter
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="doc-type">Type de document *</Label>
            <Select
              value={selectedType}
              onValueChange={(value: string) => setSelectedType(value as VehicleDocumentType)}
            >
              <SelectTrigger id="doc-type">
                <SelectValue placeholder="Sélectionner un type..." />
              </SelectTrigger>
              <SelectContent>
                {availableTypes.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    Tous les types de documents existent déjà
                  </div>
                ) : (
                  availableTypes.map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              onClick={handleTypeSelect}
              disabled={!selectedType || availableTypes.length === 0}
              className="flex-1"
            >
              Continuer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddAndLinkVehicleDocumentDialog;
